/**
 * Markdown Export Utilities
 *
 * Functions for exporting ZFS Calculator scenarios as formatted Markdown tables
 * for use in forum posts (Reddit, ServeTheHome, etc.)
 */

import type { PoolScenario } from '../types';
import { calculateCapacity } from '../core/capacity';
import { calculateReliability } from '../core/reliability';
import { calculateEconomics } from '../core/economics';

/**
 * Generate a Markdown table comparing all saved scenarios
 *
 * @param scenarios - Array of saved scenarios to export
 * @returns Formatted Markdown string
 */
export function exportScenariosToMarkdown(scenarios: PoolScenario[]): string {
  if (scenarios.length === 0) {
    return '# No Scenarios to Export\n\nNo scenarios saved yet.';
  }

  const header = `# ZFS Calculator Scenarios

Generated: ${new Date().toLocaleDateString()}

## Comparison

| Scenario | RAID | Drives | Drive Size | Raw (TiB) | Usable (TiB) | Efficiency | Cost | Cost/TiB | Risk (%) |
|----------|------|--------|------------|-----------|--------------|------------|------|----------|----------|
`;

  // Calculate results for each scenario and build table rows
  const rows = scenarios.map(s => {
    const capacityResults = calculateCapacity(s.topology, s.drive, s.zfs, s.utilization);
    const reliabilityResults = calculateReliability(
      s.topology,
      s.drive,
      s.utilization,
      capacityResults.finalUsableTiB
    );
    const economicsResults = calculateEconomics(
      s.topology,
      s.drive,
      s.economics,
      capacityResults
    );

    return [
      s.name,
      s.topology.raidLevel.toUpperCase(),
      `${s.topology.drivesPerVdev}D×${s.topology.vdevCount}V`,
      `${s.drive.driveSizeTB}TB`,
      capacityResults.rawTiB.toFixed(2),
      capacityResults.practicalUsableTiB.toFixed(2),
      `${capacityResults.practicalUsablePercent.toFixed(1)}%`,
      `$${economicsResults.hardwareCost.toFixed(0)}`,
      `$${economicsResults.costPerUsableTB.toFixed(0)}`,
      `${(reliabilityResults.totalDataLossProbability * 100).toFixed(4)}%`,
    ].join(' | ');
  }).join('\n');

  // Build detailed scenario sections
  const details = scenarios.map(s => {
    const capacityResults = calculateCapacity(s.topology, s.drive, s.zfs, s.utilization);
    const reliabilityResults = calculateReliability(
      s.topology,
      s.drive,
      s.utilization,
      capacityResults.finalUsableTiB
    );
    const economicsResults = calculateEconomics(
      s.topology,
      s.drive,
      s.economics,
      capacityResults
    );

    return `
### ${s.name}

**Configuration:**
- RAID Level: ${s.topology.raidLevel.toUpperCase()}
- Drives: ${s.topology.drivesPerVdev} per vdev × ${s.topology.vdevCount} vdevs = ${s.topology.drivesPerVdev * s.topology.vdevCount} drives total
- Drive Size: ${s.drive.driveSizeTB} TB
- Drive Cost: $${s.drive.driveCost}
- Pool Fill: ${s.utilization.poolFillPercent}%

**Results:**
- Raw Capacity: ${capacityResults.rawTiB.toFixed(2)} TiB
- Practical Usable: ${capacityResults.practicalUsableTiB.toFixed(2)} TiB (${capacityResults.practicalUsablePercent.toFixed(1)}%)
- Hardware Cost: $${economicsResults.hardwareCost.toFixed(0)}
- Cost per TiB: $${economicsResults.costPerUsableTB.toFixed(0)}
- Resilver Time: ${reliabilityResults.resilverTimeHours.toFixed(1)} hours (${reliabilityResults.resilverTimeDays.toFixed(1)} days)
- Data Loss Risk: ${(reliabilityResults.totalDataLossProbability * 100).toFixed(4)}%
- Mean Time to Pool Loss: ${reliabilityResults.mttpYears.toFixed(0)} years
`;
  }).join('\n');

  const footer = `

---
*Generated by [ZFS Storage & Economics Calculator](https://github.com/yourusername/zfs-calculator)*
`;

  return header + rows + details + footer;
}
